import {
  ArrowForward,
  DeleteOutlined,
  GroupOutlined,
  PersonOutlined,
} from "@mui/icons-material";
import { Box, Typography, useTheme } from "@mui/material";
import { FC, useEffect, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useLocalization } from "../hooks/useLocalization";
import { Routes } from "../types/NavigationPaths";
import { ASSIGNED_VIA_DIRECT } from "./AssignedViaCell";
import { ConfirmationDialog } from "./ConfirmationDialog";

export interface AssignedVia {
  source: string;
  sourceId: string;
}

interface ManageAccessDialogProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  sources: AssignedVia[];
  onDirectClick: () => void;
}

interface SourceItemProps {
  assignedVia: AssignedVia;
  onDirectClick: () => void;
  onGroupClick: (sourceId: string) => void;
}

const SourceItem: FC<SourceItemProps> = ({
  assignedVia,
  onDirectClick,
  onGroupClick,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();

  const isDirect = assignedVia.source === ASSIGNED_VIA_DIRECT;

  const title = isDirect
    ? t("components.manageAccessDialog.directAccess")
    : assignedVia.source;

  const Icon = isDirect ? PersonOutlined : GroupOutlined;

  const ActionIcon = isDirect ? DeleteOutlined : ArrowForward;

  const onClick = () => {
    if (isDirect) {
      onDirectClick();
    } else {
      onGroupClick(assignedVia.sourceId);
    }
  };

  return (
    <Box
      onClick={onClick}
      sx={{
        display: "flex",
        height: 40,
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 1,
        px: 1,
        cursor: "pointer",
        "&:hover": {
          backgroundColor: theme.colors.grey,
          "& .action-icon": {
            color: theme.colors.primary[5],
          },
        },
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <Icon sx={{ fontSize: 16 }} />
        <Typography
          sx={{ fontWeight: 500, fontSize: 14, color: theme.colors.text[1] }}
        >
          {title}
        </Typography>
      </Box>
      <ActionIcon
        className="action-icon"
        sx={{
          fontSize: 20,
          color: theme.colors.text[3],
        }}
      />
    </Box>
  );
};

export const ManageAccessDialog: FC<ManageAccessDialogProps> = ({
  isOpen,
  onClose,
  title,
  sources,
  onDirectClick,
}) => {
  const { t } = useLocalization();
  const theme = useTheme();
  const navigate = useNavigate();

  const sourcesRef = useRef<AssignedVia[]>(sources);

  useEffect(() => {
    if (isOpen && sources.length > 0) {
      sourcesRef.current = sources;
    }
  }, [isOpen, sources]);

  const displaySources = isOpen ? sources : sourcesRef.current;

  const sortedSources = useMemo(
    () =>
      [...displaySources].sort((a, b) => {
        if (
          a.source === ASSIGNED_VIA_DIRECT &&
          b.source !== ASSIGNED_VIA_DIRECT
        ) {
          return -1;
        }
        if (
          a.source !== ASSIGNED_VIA_DIRECT &&
          b.source === ASSIGNED_VIA_DIRECT
        ) {
          return 1;
        }

        return a.source.localeCompare(b.source);
      }),
    [displaySources],
  );

  const { companyId } = useParams();

  const navigateToUserGroupDetails = (sourceId: string) => {
    void navigate(`/companies/${companyId}${Routes.GROUPS}/${sourceId}`);
  };

  return (
    <ConfirmationDialog.Root
      fullWidth
      maxWidth="xs"
      onClose={onClose}
      open={isOpen}
      sx={{
        "& .MuiDialog-paper": {
          padding: "0px",
          borderRadius: "16px",
          maxWidth: "430px",
        },
      }}
    >
      <ConfirmationDialog.Title
        showIcon={false}
        sx={{ fontSize: 18, px: 2, pb: 3 }}
      >
        {title}
      </ConfirmationDialog.Title>
      <ConfirmationDialog.Content
        sx={{
          fontWeight: 500,
          color: theme.colors.text[1],
          px: 2,
          pb: 1,
        }}
      >
        {sortedSources.map((item) => (
          <SourceItem
            assignedVia={item}
            key={item.sourceId}
            onDirectClick={onDirectClick}
            onGroupClick={navigateToUserGroupDetails}
          />
        ))}
      </ConfirmationDialog.Content>
      <ConfirmationDialog.Actions sx={{ px: 2, pb: 2, pt: 1 }}>
        <ConfirmationDialog.Button
          onClick={onClose}
          sx={{
            color: theme.colors.primary[5],
          }}
          variant="secondary"
        >
          {t("components.manageAccessDialog.cancel")}
        </ConfirmationDialog.Button>
      </ConfirmationDialog.Actions>
    </ConfirmationDialog.Root>
  );
};
